import Widget from "./Widget";
import sources from "../config/sources";
import { formatDate, formatNumber } from "../helpers/formatters";

import { tmpl, activateTabs } from "../../templates/tabbedwidget.js";
import buildDataTable from "../buildDataTable.js";

/* charting */
import * as echarts from "echarts/core";
import { LineChart, PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
} from "echarts/components";
import { LabelLayout, UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  LineChart,
  PieChart,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
]);

import { topN } from "../helpers/utils.js";
export default class TabbedWidget extends Widget {
  constructor(feed, dimension) {
    super();

    if (
      !Object.prototype.hasOwnProperty.call(sources, feed) ||
      !Object.prototype.hasOwnProperty.call(sources[feed], dimension)
    ) {
      throw new Error(`Invalid feed/dimension: ${feed}/${dimension}`);
    }

    this.datasources = sources[feed][dimension];

    this.graph;
    this.all = [];
    this.metricdata = {
      highest: [],
      upwards: [],
      downwards: [],
    };
    this.selectedMetric = "highest";
    this.searched = []; // acts as a filter of the all data - filters search matches
  }

  init(el) {
    this.div = document.createElement("div");
    this.div.classList.add("sh-widget", "sh-tabbed-widget", this._theme);
    this.setTemplate(tmpl);
    this.setTitle(this.datasources["title"]);
    this.setTitleTooltip(this.datasources["titleTooltip"]);

    activateTabs(this.div);
    this.div.addEventListener(
      "tab.click",
      (e) => {
        if (e.detail === "sh-line") {
          this.buildTimelinePane();
        }
        if (e.detail === "sh-chart") {
          this.buildChartPane();
        }
      },
      false
    );
    el.replaceWith(this.div);

    this.buildTablePane();
  }

  buildTablePane() {
    let tableEl = this.div.querySelector('[data-element="table"]');
    const data = this.getData(DATA_URL + this.datasources["table"]["url"]);
    data.then((data) => {
      const cols = this.datasources["table"]["columns"];
      buildDataTable(tableEl, data, cols);

      // data stored for other panes
      this.all = data;
      this.metricdata["highest"] = topN(data, "value", 10);
      this.metricdata["upwards"] = topN(data, "change", 10);
      this.metricdata["downwards"] = topN(data, "change", 10, true);

      this.activateTooltips();
      this.clearLoading();
    });
  }

  buildTimelinePane() {
    const data = this.getData(DATA_URL + this.datasources["timeline"]["url"]);
    data.then((data) => {
      const graphElement = this.div.querySelector('[data-element="graph"]');
      this.graph = echarts.init(graphElement);
      this.graph.showLoading();
      const yfield = this.datasources["timeline"]["yfield"];

      this.drawLines(this.graph, data.x, data.y, yfield);

      /**
       * Event listener for metric radio buttons
       */
      const _this = this;
      const metricRadios = [].slice.call(
        this.div.querySelectorAll("[data-metric-option]")
      );
      metricRadios.map(function (el) {
        el.addEventListener(
          "click",
          function (event) {
            _this.selectedMetric = event.target.value;
            _this.drawLines(_this.graph, data.x, data.y, yfield);
          },
          false
        );
      });

      /**
       * Event listener for search box
       */
      const textSearch = this.div.querySelector(".sh-line-search");
      textSearch.addEventListener(
        "keyup",
        function (event) {
          const search = event.target.value;
          if (search !== "") {
            // searches on the defined "yfield" from datasources
            _this.searched = _this.all.filter((item) =>
              item[yfield].toLowerCase().includes(search.toLowerCase())
            );
          } else {
            _this.searched = [];
          }
          _this.drawLines(_this.graph, data.x, data.y, yfield);
        },
        false
      );
    });
  }

  drawLines(graph, x, y, yfield) {
    let ySeries = [];

    // filter by selected metric
    const metricFilter =
      this.searched.length > 0
        ? this.searched.map((item) => item[yfield])
        : this.metricdata[this.selectedMetric].map((item) => item[yfield]);

    for (let [key, value] of Object.entries(y)) {
      // filter by the selected metric:
      if (metricFilter.includes(key)) {
        value = value.map((v, i) => [x[i], v]); // puts the x-axis value into the array
        ySeries.push({
          name: key,
          type: "line",
          data: value,
          showSymbol: false,
          lineStyle: {
            width: 4,
          },
        });
      }
    }

    const formatter = Intl.NumberFormat("en", { notation: "compact" });

    graph.setOption(
      {
        grid: {
          width: "80%",
          top: 50,
          left: 0,
          bottom: 0,
          containLabel: true,
        },
        legend: {
          icon: "rect",
          orient: "vertical",
          right: 10,
          top: "center",
          inactiveColor: "red",
          textStyle: {
            color: "#fff",
            width: 140,
            overflow: "truncate",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: "line",
          backgroundColor: "transparent",
          borderWidth: 0,
          padding: 0,
          formatter: function (params) {
            const xname = params[0].value[0];
            let ylist = "";
            params.forEach(function (series) {
              ylist += `<div class="sh-widget-tooltip-row"><div><span class="sh-widget-line-color" style="background-color:${
                series.color
              }"></span> ${series.seriesName}:</div> <div>${formatNumber(
                series.value[1]
              )}</div></div>`;
            });
            return `<div class="sh-widget-tooltip">
                              <div class="sh-tooltip-header">${formatDate(
                                xname
                              )}</div>
                              <div class="sh-tooltip-body">${ylist}
                              </div>
                          </div>`;
          },
        },
        xAxis: {
          type: "time",
          minInterval: 1,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: "#fff",
            },
          },
          axisLabel: {
            formatter: "{MMM}, {dd} {yyyy}",
            color: "#fff",
            fontSize: 12,
            fontFamily: "sans-serif",
            fontWeight: 700,
          },
        },
        yAxis: {
          min: "dataMin",
          type: "log",
          logBase: 2,
          splitLine: {
            lineStyle: {
              color: "rgba(255, 255, 255, 0.3)",
            },
          },
          axisLabel: {
            formatter: function (d) {
              return formatter.format(d);
            },
            color: "#fff",
            fontSize: 12,
            fontFamily: "sans-serif",
            fontWeight: 700,
          },
        },
        series: ySeries,
      },
      true
    );
    graph.hideLoading();
  }

  buildChartPane() {
    // work out the %s
    let total = this.all.reduce((a, b) => a + b.value, 0);
    let highestChart = [];
    let allChart = [];
    let otherValue = 0;
    const yfield = this.datasources["chart"]["yfield"];
    const highestTableItems = this.metricdata["highest"].map(
      (item) => item[yfield]
    );

    /**
     * Sorting the pie chart data and table data
     */

    // eslint-disable-next-line no-unused-vars
    for (let [key, value] of Object.entries(this.all)) {
      let row = {
        rank: value.rank,
        name: value[yfield],
        value: value.value,
        percent: (value.value / total) * 100,
      };

      allChart.push(row);
      if (highestTableItems.includes(value[yfield])) {
        highestChart.push(row);
      } else {
        otherValue += value.value;
      }
    }

    highestChart.push({
      rank: "--",
      name: "Other",
      value: otherValue,
      percent: (otherValue / total) * 100,
    });

    const pieElement = this.div.querySelector('[data-element="pie-chart"]');
    this.pie = echarts.init(pieElement);

    this.pie.setOption({
      grid: {
        width: "85%",
        top: 50,
        left: 0,
        bottom: 0,
        containLabel: true,
      },
      legend: {
        icon: "rect",
        orient: "vertical",
        right: 10,
        top: "center",
        inactiveColor: "red",
        textStyle: {
          color: "#fff",
          width: 120,
          overflow: "truncate",
        },
      },
      tooltip: {
        trigger: "item",
        backgroundColor: "transparent",
        borderWidth: 0,
        padding: 0,
        formatter: function (params) {
          return `<div class="sh-widget-tooltip">
                          <div class="sh-tooltip-header">${params.name}</div>
                          <div class="sh-tooltip-body">${formatNumber(
                            params.data.value
                          )} (${params.percent}%)</div>
                        </div>
                    `;
        },
      },
      series: [
        {
          center: ["35%", "50%"],
          type: "pie",
          radius: ["30%", "75%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 0,
            borderColor: "#142741", // @todo this will need a "light" mode
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          labelLine: {
            show: false,
          },
          data: highestChart,
        },
      ],
    });

    const chartTableEl = this.div.querySelector('[data-element="chart-table"]');
    let cols = this.datasources["chart"]["columns"];
    buildDataTable(chartTableEl, allChart, cols);
  }
}
